import { useCallback, useEffect, useState } from 'react'; // Adicionado useCallback e useEffect
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { LayoutFrame, NavButton } from 'app-layout-frame';
import {
    DropdownList,
    DropdownListDivider,
    DropdownListItem,
    IconBell,
    IconClock,
    IconSignOut,
    IconUserLight,
    IconVolume,
    IconVolumeMute,
    Inline,
    PersistentBanner,
    Spinner
} from '@7shifts/sous-chef';
import Logo from 'sharedComponents/Logo';
import { useAuthContext } from './contexts/AuthContext';
import { __ } from './i18n';
import { getRemainingDaysToExpire } from './domain/membership';
import { getNavItems } from './domain/navItems';
import {
    WhatsAppProvider,
    useWhatsContext
} from './contexts/WhatsAppContext/WhatsAppContext';
import UserStatusBullet from 'sharedComponents/UserStatusBullet';
import { changeStatus } from './api/usersApi';
import NotificationsProvider from 'sharedComponents/NotificationsProvider';
import NoChannelPersistentBanner from 'sharedComponents/NoChannelPersistentBanner';
import { User } from 'types';
import { useNotificationContext } from 'sharedComponents/NotificationsProvider/NotificationContext';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { FeaturesProvider } from 'contexts/FeaturesContext/FeaturesContext';
import IdleUserTimeoutModal from 'modules/Auth/components/TimeoutModal';
import { getIdleTimeoutInMinutesSetting } from 'domain/settings';
import ImpersonateBanner from 'sharedComponents/ImpersonateBanner/ImpersonateBanner';
import { useInactivityTimeout } from 'hooks/useInactivityTimeout';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore this does not have types :/
// import useSound from 'use-sound';
// import alertSound from './assets/sound.mp3';

const App = () => {
    const { handleLogout, isAuth, loading, user } = useAuthContext();
    const navigate = useNavigate();

    // Memoriza o onLogout para evitar renderizações desnecessárias nos contextos
    const onLogout = useCallback(() => {
        handleLogout().then(() => {
            navigate('/login?logged_out=1');
        });
    }, [handleLogout, navigate]);

    if (loading) return <Spinner block />;

    if (!isAuth && !loading) {
        const path = window.location.pathname;
        const search = window.location.search;
        if (path && path.indexOf('login') === -1) {
            return <Navigate to={`/login?urlReturn=${path}${search}`} />;
        }
        if (search && search.indexOf('logged_out') > 0) {
            return <Navigate to={`/login?logged_out=1`} />;
        }
        return <Navigate to={`/login`} />;
    }

    if (!user)
        throw new Error('User is not present! This should never happen!');

    return (
        <NotificationsProvider user={user}>
            <FeaturesProvider>
                <WhatsAppProvider>
                    <Layout user={user} onLogout={onLogout} />
                </WhatsAppProvider>
            </FeaturesProvider>
        </NotificationsProvider>
    );
};

const Layout = ({ user, onLogout }: { user: User; onLogout: () => void }) => {
    const { whatsApps } = useWhatsContext();
    const remainingDaysToExpire = getRemainingDaysToExpire();
    const navItems = getNavItems(user, whatsApps);
    const [statusAvailable, setStatusAvailable] = useState(
        user.statusAvailable
    );
    const [, setUserSoundEnabledFromStorage] = useLocalStorage(
        'userSoundEnabled',
        true
    );
    const [, setUserPopupEnabledFromStorage] = useLocalStorage(
        'userPopupEnabled',
        true
    );
    const {
        setUserNotificationEnabled,
        setUserNotificationSoundEnabled,
        userNotificationEnabled,
        userNotificationSoundEnabled
    } = useNotificationContext();

    const isTicketsRoute = window.location.pathname.includes('/tickets');
    const isKanbanRoute = window.location.pathname.includes('/kanban');

    const toggleUserNotificationSound = (newValue?: boolean) => {
        const value =
            newValue !== undefined ? newValue : !userNotificationSoundEnabled;
        setUserSoundEnabledFromStorage(value);
        setUserNotificationSoundEnabled(value);
    };

    const handleUserNotification = () => {
        const newValue = !userNotificationEnabled;
        setUserNotificationEnabled(newValue);
        setUserPopupEnabledFromStorage(newValue);
        if (!newValue) toggleUserNotificationSound(false);
    };

    const { settings, handleLogout } = useAuthContext();
    const idleTimeoutInMinutes = getIdleTimeoutInMinutesSetting(settings);
    const [isTimeoutModalEnabled, setIsTimeoutModalEnabled] = useState(false);

    // 1. Função de timeout memorizada para evitar disparos falsos no hook/modal
    const onInactivityDetected = useCallback(() => {
        const channel = new BroadcastChannel('inactivity-channel');
        channel.postMessage({
            type: 'LOGOUT_DUE_TO_INACTIVITY',
            time: Date.now()
        });
        setIsTimeoutModalEnabled(true);
    }, []);

    // 2. Listener para fechar outras abas se uma detectar inatividade
    useEffect(() => {
        const channel = new BroadcastChannel('inactivity-channel');
        channel.onmessage = (event) => {
            if (event.data?.type === 'LOGOUT_DUE_TO_INACTIVITY') {
                setIsTimeoutModalEnabled(true);
            }
        };
        return () => channel.close();
    }, []);

    useInactivityTimeout({
        timeout: idleTimeoutInMinutes,
        onTimeout: onInactivityDetected
    });

    // 3. Função de logout imediato (sem o delay de 1000ms que causava a sensação de travamento)
    const handleConfirmTimeout = useCallback(() => {
        handleLogout();
    }, [handleLogout]);

    return (
        <>
            <LayoutFrame
                navItems={navItems}
                banner={
                    <>
                        <ImpersonateBanner />
                        {user.profile === 'admin' && (
                            <NoChannelPersistentBanner />
                        )}
                        {user.profile === 'admin' &&
                            remainingDaysToExpire <= 5 &&
                            remainingDaysToExpire >= 0 && (
                                <PersistentBanner>
                                    {__('main.membershipExpires', {
                                        count: remainingDaysToExpire
                                    })}
                                </PersistentBanner>
                            )}
                        {user.profile === 'admin' &&
                            remainingDaysToExpire < 0 && (
                                <PersistentBanner theme="danger">
                                    {__('main.membershipExpired', {
                                        count: remainingDaysToExpire * -1
                                    })}
                                </PersistentBanner>
                            )}
                    </>
                }
                appLogo={<Logo />}
                companyName={user.company.name}
                userProfileImageURL=""
                accountMenuDropdown={
                    <DropdownList>
                        {statusAvailable ? (
                            <DropdownListItem
                                prefix={<IconClock />}
                                onClick={() =>
                                    changeStatus(user.id, 'away').then(() =>
                                        setStatusAvailable(false)
                                    )
                                }
                            >
                                {__('main.setAsAway')}
                            </DropdownListItem>
                        ) : (
                            <DropdownListItem
                                prefix={<IconClock />}
                                onClick={() =>
                                    changeStatus(user.id, 'available').then(
                                        () => setStatusAvailable(true)
                                    )
                                }
                            >
                                {__('main.setAsAvailable')}
                            </DropdownListItem>
                        )}
                        <DropdownListDivider />
                        <DropdownListItem
                            href="/my-account"
                            prefix={<IconUserLight />}
                        >
                            {__('main.myAccount')}
                        </DropdownListItem>
                        <DropdownListItem
                            onClick={onLogout}
                            prefix={<IconSignOut />}
                        >
                            {__('main.logOut')}
                        </DropdownListItem>
                    </DropdownList>
                }
                actions={[
                    <Inline key="app-actions-inline">
                        <UserStatusBullet statusAvailable={statusAvailable} />
                        {userNotificationEnabled && (
                            <NavButton
                                onClick={() => toggleUserNotificationSound()}
                                tooltipContent={
                                    userNotificationSoundEnabled
                                        ? __('main.setSoundDisable')
                                        : __('main.setSoundEnabled')
                                }
                            >
                                {userNotificationSoundEnabled ? (
                                    <IconVolume color="var(--color-mint-500)" />
                                ) : (
                                    <IconVolumeMute color="var(--color-banana-500)" />
                                )}
                            </NavButton>
                        )}
                        <NavButton
                            onClick={handleUserNotification}
                            tooltipContent={
                                userNotificationEnabled
                                    ? __('main.setPopupDisable')
                                    : __('main.setPopupEnabled')
                            }
                        >
                            {userNotificationEnabled ? (
                                <IconBell color="var(--color-mint-500)" />
                            ) : (
                                <IconBell color="var(--color-banana-500)" />
                            )}
                        </NavButton>
                    </Inline>
                ]}
            >
                {isTicketsRoute || isKanbanRoute ? (
                    <Outlet />
                ) : (
                    <div className="padding-bottom-40">
                        <Outlet />
                    </div>
                )}
            </LayoutFrame>

            {idleTimeoutInMinutes > 0 && isTimeoutModalEnabled === true && (
                <IdleUserTimeoutModal
                    minutes={idleTimeoutInMinutes}
                    onContinue={() => {
                        setIsTimeoutModalEnabled(false);
                    }}
                    onTimeout={handleConfirmTimeout}
                />
            )}
        </>
    );
};

export default App;
